import { useState, useEffect, useMemo, useRef } from 'react';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';
import { Badge } from './badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from './command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog';
import { 
  Check, 
  ChevronsUpDown, 
  Plus, 
  Search,
  User,
  Building2,
  Loader2
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SearchableContactSelect - Searchable dropdown with "Add New" option for customers/vendors
 * @param {string} type - 'customer' or 'vendor'
 * @param {Array} contacts - List of contacts to select from
 * @param {string} value - Currently selected contact_id
 * @param {function} onValueChange - Callback when selection changes
 * @param {string} workspaceId - Current workspace ID
 * @param {function} onContactCreated - Callback when a new contact is created (receives the new contact)
 * @param {string} placeholder - Placeholder text
 * @param {string} className - Additional CSS classes
 * @param {string} testId - data-testid attribute
 */
export function SearchableContactSelect({
  type = 'customer',
  contacts = [],
  value,
  onValueChange,
  workspaceId,
  onContactCreated,
  placeholder = 'Select...',
  className,
  testId
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newContact, setNewContact] = useState({
    name: '',
    email: '',
    phone: '',
    street_address: '',
    city: '',
    state: '',
    zip_code: ''
  });

  const isCustomer = type === 'customer';
  const label = isCustomer ? 'Customer' : 'Vendor';
  const contactType = isCustomer ? 'customer' : 'vendor';

  // Filter contacts based on search
  const filteredContacts = useMemo(() => {
    if (!search) return contacts;
    const lower = search.toLowerCase();
    return contacts.filter(c => 
      c.name?.toLowerCase().includes(lower) ||
      c.email?.toLowerCase().includes(lower)
    );
  }, [contacts, search]);

  // Find selected contact name
  const selectedContact = useMemo(() => 
    contacts.find(c => c.contact_id === value),
    [contacts, value]
  );

  // Handle creating new contact
  const handleCreateContact = async () => {
    if (!newContact.name.trim()) {
      toast.error('Name is required');
      return;
    }

    setCreating(true);
    try {
      const response = await fetch(
        `${API_URL}/api/contacts?workspace_id=${workspaceId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...newContact,
            contact_type: contactType
          })
        }
      );

      if (response.ok) {
        const createdContact = await response.json();
        toast.success(`${label} created`);
        
        // Close dialog and select the new contact
        setShowNewDialog(false);
        setNewContact({ name: '', email: '', phone: '', street_address: '', city: '', state: '', zip_code: '' });
        
        // Notify parent to refresh contacts list
        if (onContactCreated) {
          onContactCreated(createdContact);
        }
        
        // Select the new contact
        onValueChange(createdContact.contact_id);
      } else {
        const error = await response.json();
        toast.error(error.detail || `Failed to create ${label.toLowerCase()}`);
      }
    } catch (error) {
      toast.error(`Failed to create ${label.toLowerCase()}`);
    } finally {
      setCreating(false);
    }
  };

  // Pre-fill name from search when opening dialog
  const handleOpenNewDialog = () => {
    setNewContact(prev => ({
      ...prev,
      name: search
    }));
    setShowNewDialog(true);
    setOpen(false);
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn("w-full justify-between", className)}
            data-testid={testId}
          >
            {selectedContact ? (
              <span className="flex items-center gap-2 truncate">
                {isCustomer ? <User className="w-4 h-4 text-muted-foreground" /> : <Building2 className="w-4 h-4 text-muted-foreground" />}
                {selectedContact.name}
              </span>
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput 
              placeholder={`Search ${label.toLowerCase()}s...`}
              value={search}
              onValueChange={setSearch}
              data-testid={`${testId}-search`}
            />
            <CommandList>
              {/* Add New Button at top */}
              <CommandGroup>
                <CommandItem
                  onSelect={handleOpenNewDialog}
                  className="text-primary cursor-pointer"
                  data-testid={`add-new-${type}-btn`}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add New {label}
                  {search && <span className="ml-1 text-muted-foreground">"{search}"</span>}
                </CommandItem>
              </CommandGroup>
              
              <CommandSeparator />
              
              {/* Contact List */}
              <CommandGroup heading={`${label}s`}>
                {filteredContacts.length === 0 ? (
                  <CommandEmpty>
                    No {label.toLowerCase()}s found.
                    <Button 
                      variant="link" 
                      className="p-0 h-auto ml-1"
                      onClick={handleOpenNewDialog}
                    >
                      Create one?
                    </Button>
                  </CommandEmpty>
                ) : (
                  filteredContacts.map((contact) => (
                    <CommandItem
                      key={contact.contact_id}
                      value={contact.contact_id}
                      onSelect={() => {
                        onValueChange(contact.contact_id);
                        setOpen(false);
                        setSearch('');
                      }}
                      data-testid={`${type}-option-${contact.contact_id}`}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          value === contact.contact_id ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <div className="flex flex-col">
                        <span>{contact.name}</span>
                        {contact.email && (
                          <span className="text-xs text-muted-foreground">{contact.email}</span>
                        )}
                      </div>
                    </CommandItem>
                  ))
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* New Contact Dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              <span className="flex items-center gap-2">
                {isCustomer ? <User className="w-5 h-5" /> : <Building2 className="w-5 h-5" />}
                New {label}
              </span>
            </DialogTitle>
            <DialogDescription>
              Create a new {label.toLowerCase()} to use in this {isCustomer ? 'invoice' : 'bill'}.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="contact-name">Name *</Label>
              <Input
                id="contact-name"
                value={newContact.name}
                onChange={(e) => setNewContact(prev => ({ ...prev, name: e.target.value }))}
                placeholder={isCustomer ? "Customer name" : "Vendor/Company name"}
                data-testid={`new-${type}-name`}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="contact-email">Email</Label>
              <Input
                id="contact-email"
                type="email"
                value={newContact.email}
                onChange={(e) => setNewContact(prev => ({ ...prev, email: e.target.value }))}
                placeholder="email@example.com"
                data-testid={`new-${type}-email`}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="contact-phone">Phone</Label>
              <Input
                id="contact-phone"
                value={newContact.phone}
                onChange={(e) => setNewContact(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="(555) 123-4567"
                data-testid={`new-${type}-phone`}
              />
            </div>

            {/* Address Section */}
            <div className="pt-2 border-t">
              <p className="text-sm font-medium text-muted-foreground mb-3">Address (Optional)</p>
              
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="contact-street">Street Address</Label>
                  <Input
                    id="contact-street"
                    value={newContact.street_address}
                    onChange={(e) => setNewContact(prev => ({ ...prev, street_address: e.target.value }))}
                    placeholder="123 Main Street"
                    data-testid={`new-${type}-street`}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="contact-city">City</Label>
                    <Input
                      id="contact-city"
                      value={newContact.city}
                      onChange={(e) => setNewContact(prev => ({ ...prev, city: e.target.value }))}
                      placeholder="City"
                      data-testid={`new-${type}-city`}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-state">State</Label>
                    <Input
                      id="contact-state"
                      value={newContact.state}
                      onChange={(e) => setNewContact(prev => ({ ...prev, state: e.target.value }))}
                      placeholder="CA"
                      maxLength={2}
                      data-testid={`new-${type}-state`}
                    />
                  </div>
                </div>

                <div className="space-y-2 max-w-[150px]">
                  <Label htmlFor="contact-zip">ZIP Code</Label>
                  <Input
                    id="contact-zip"
                    value={newContact.zip_code}
                    onChange={(e) => setNewContact(prev => ({ ...prev, zip_code: e.target.value }))}
                    placeholder="12345"
                    maxLength={10}
                    data-testid={`new-${type}-zip`}
                  />
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateContact} 
              disabled={creating || !newContact.name.trim()}
              data-testid={`create-${type}-btn`}
            >
              {creating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Create {label}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default SearchableContactSelect;
